import React, { useCallback } from 'react';
import { useResponsive } from '@sg/garnish';

import type { CreditCardForm } from '@order/components';
import {
  CreditCardFormContent,
  Modal,
  NotFoundView,
  TransparentModal,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateBack } from '../../navigation';
import { useSweetpassPaymentMethodAdd } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassPaymentMethodAddScreen = () => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const {
    isSweetpassEnabled,
    isLoading: isAdding,
    addPaymentMethod,
  } = useSweetpassPaymentMethodAdd();

  const navigateBack = useNavigateBack();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleSavePaymentMethod = useCallback(
    async (_form: CreditCardForm, nonce: string) => {
      const response = await addPaymentMethod(nonce);
      const responseData = response?.addSubscriptionPaymentMethod;
      const responseTypename = responseData?.__typename;

      const hasSuccessfullyAddedPaymentMethod =
        responseTypename === 'AddSubscriptionPaymentMethodSuccessResponse';

      if (!hasSuccessfullyAddedPaymentMethod) return;

      navigateBack();
    },
    [addPaymentMethod, navigateBack],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!isSweetpassEnabled) {
    return (
      <TransparentModal size="small" onClose={navigateBack}>
        <NotFoundView />
      </TransparentModal>
    );
  }

  return (
    <Modal
      fitHeight
      visible
      accessibilityLabel={t('sweetpass.signed-in.add-payment-method-title')}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={navigateBack}
    >
      <Modal.Header onClose={navigateBack}>
        {t('sweetpass.signed-in.add-payment-method-title')}
      </Modal.Header>

      <CreditCardFormContent
        isVisible
        vault="subscription"
        mode="add"
        excludedFields={['default', 'nickname']}
        isSaving={isAdding}
        onSave={handleSavePaymentMethod}
      />
    </Modal>
  );
};
